import { uniq } from 'lodash'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import type { View } from 'modules/shift_management/lib/types'
import RestDaysApi from 'modules/shift_management/store/api/collections/restDays'
import RestDay, { RestDayPayload } from 'modules/shift_management/store/internal/models/RestDay'

import type { RestDayParams } from '../lib/buildAddRestDaysParameters'
import { useShiftsRows } from './useShiftsRows'
import { useUI } from './useUI'

const trackAddEvent = (restDays: RestDay[], view: View) => {
  const employeeCount = uniq(restDays.map((restDay) => restDay.employeeId)).length

  return track(trackingEvents.shiftManagement.addRestDays, {
    number_of_impacted_employees: employeeCount,
    number_of_impacted_rest_days: restDays.length,
    view: view.viewType,
    view_type_by: view.groupType,
  })
}

export interface Props {
  params: RestDayParams[]
  onSuccess?: () => void
}

const useAddRestDays = ({ params, onSuccess }: Props) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchUI] = useUI()

  const { refreshStats } = useRefreshStats(view.startAt, view.endAt)

  const { running: submitting, performAction: onSubmit } = useAsyncAction(
    async () => {
      const result = await asyncAction(
        () =>
          RestDaysApi.rpc('bulk_create', {
            rest_days: params,
          }),
        {
          successFlash: false,
        }
      )

      if (isSuccessful(result)) {
        const { value: newRestDays }: { value: RestDayPayload[] } = result
        const restDays = newRestDays.map((restDay) => RestDay.buildFromRequest(restDay))

        dispatchUI({ type: 'REFRESHING_STATS' })
        dispatchSchedule({ type: 'ADD_REST_DAYS', restDays })
        dispatchShiftsRows({ type: 'ADD_REST_DAYS', restDays })

        if (onSuccess) onSuccess()

        trackAddEvent(restDays, view)
        await refreshStats(shiftsRows)
      }
    },
    {
      successMessage: i18n.t('shift_management.actions.add_rest_day_success'),
      errorFlash: false,
    }
  )

  return {
    submitting,
    onSubmit,
  }
}

export { useAddRestDays }
